import {AfterViewInit, Component, TemplateRef, ViewChild} from '@angular/core';
import {OpenLinkInvitee} from "src/app/data/interefaces/panel.interfaces";
import {KpiSidebarItem} from "src/app/shared/ag-kpi-sidebar/ag-kpi-sidebar.component.ds";
import {Util} from "src/app/data/util/util";
import {ResponseStatus, YesNoValue} from "src/app/data/enums/data.enums";
import {ActivatedRoute} from "@angular/router";
import {OpenLinkInviteesService} from "src/app/data/panel/open.link.invitees.service";
import {SurveySpecService} from "src/app/data/survey-spec/survey.spec.service";
import {FilterChangedEvent, FirstDataRenderedEvent, SelectionChangedEvent, SelectionColumnDef, ViewportChangedEvent} from "@ag-grid-community/core";
import {TableBaseComponent} from "@pages/table.base.component";
import {marketCodes, pageContext, periods, surveyCodes, tableColumns} from "@pages/panel/open-link-invitees/open-link-invitees.component.ds";
import {QuilliupForm} from "@data/enums/quilliup.enum";
import {ResultsetThresholds} from "@data/interefaces/data.interfaces";
import {ReferenceService} from "@data/reference/reference.service";
import {LookupService} from "@data/lookup/lookup.service";
import {AgGridService} from "@shared/services/ag-grid.service";
import {FeatureComponentMode} from "@core/enums/core.enums";

@Component({
  selector: 'app-open-link-invitees',
  templateUrl: './open-link-invitees.component.html',
  styleUrls: ['./open-link-invitees.component.scss'],
  providers: [AgGridService]
})
export class OpenLinkInviteesComponent extends TableBaseComponent implements AfterViewInit {
  rowData: OpenLinkInvitee[] = [];
  selectedRows: OpenLinkInvitee[] = [];
  selectedFeatureMode!: FeatureComponentMode;
  auxLinks: Map<string, string>;
  showSkeleton: boolean = true;
  kpiSidebar: KpiSidebarItem[] = [];
  refreshComplete: boolean = true;
  isFilterActive: boolean = false;
  @ViewChild('footerActionButtonPanel') footerActionButtonPanel: TemplateRef<any> | undefined;

  isBulkOperationAllowed: boolean = false;
  showTaskComponent: boolean = false;

  protected readonly util = Util;
  protected readonly pageContext = pageContext;
  protected override readonly tableColumns = tableColumns
  protected readonly responseStatus = ResponseStatus;
  protected readonly QuilliupForm = QuilliupForm;

  constructor(private _activatedRoute: ActivatedRoute, private _openLinkInviteService: OpenLinkInviteesService, private _surveySpecService: SurveySpecService, private _referenceService: ReferenceService, private _lookupService: LookupService) {
    super();
    this.auxLinks = new Map([['/home/welcome', 'Home']]);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.initiatePageState(this._activatedRoute.snapshot.queryParamMap);
    this.loadPageData().catch(error => this.displayNotificationMessage('error', JSON.stringify(error)));
    this.loadFilterData();
    this.updatePageContext();
  }

  ngAfterViewInit() {
  }

  onLiveDataFilterChange(forceReload: boolean = false): void {
    if (forceReload) {
      this.loadPageData().catch(error => this.displayNotificationMessage('error', JSON.stringify(error)));
    }
    this.updateToPageContextUrl();
  }

  async loadPageData() {
    const periods = this.pageContext.p_f || '';
    const marketCodes = this.pageContext.mc_f || '';
    const surveyCodes = this.pageContext.sc_f || '';
    let thresholds: ResultsetThresholds | undefined;
    this.refreshComplete = false;
    this.displayProgressBar(true);
    await this._openLinkInviteService.getOpenLinkInviteesThresholds(periods, marketCodes, surveyCodes).then(result => {
      thresholds = result;
      this.setRowTemplateMessage(thresholds);
    }).catch(error => {
        this.displayNotificationMessage('error', JSON.stringify(error))
        this.displayProgressBar(false);
        this.refreshComplete = true;
      }
    );

    if (thresholds && !thresholds.hasTooMany) {
      await this._openLinkInviteService.getOpenLinkInvitees(periods, marketCodes, surveyCodes).then(result => {
        this.rowData = result;
      }).catch(error => this.displayNotificationMessage('error', JSON.stringify(error))
      ).finally(() => {
          this.displayProgressBar(false);
          this.refreshComplete = true;
        }
      );
    } else {
      this.refreshComplete = true;
      this.rowData = [];
      this.displayProgressBar(false);
    }
  }

  loadFilterData() {
    this._surveySpecService.getSurveys().then(result => {
      result.forEach(item => surveyCodes.push(item.surveyCode));
    });
    this._referenceService.getAllMarkets().then(result => {
      result.forEach(item => marketCodes.push(item.marketCode));
    });
    this._lookupService.getPeriods().then(result => {
      result.forEach(item => periods.push(item.period));
    });
  }

  onRowSelectionChanged(event: SelectionChangedEvent) {
    this.refreshGridHeaderOnSelectionChanged(event);
    this.selectedRows = this.getSelectedRows();
    this.isBulkOperationAllowed = this.selectedRows.length != 0 && this.selectedRows.every(item => item.surveyCode === this.selectedRows[0].surveyCode);
  }

  async refreshView() {
    await this.loadPageData().catch(error => this.displayNotificationMessage('error', JSON.stringify(error)));
  }

  onBulkMailoutClicked() {
    this.selectedFeatureMode = FeatureComponentMode.BULK_MAILOUT;
    this.showTaskComponent = true;
  }

  onBulkEditClicked() {
    this.selectedFeatureMode = FeatureComponentMode.BULK_EDIT;
    this.showTaskComponent = true;
  }

  goBack() {
    this.showTaskComponent = false;
    this.refreshView();
  }

  onFilterChanged(event: FilterChangedEvent<any>) {
    const updateLiveDataFilter = this.isUpdateLiveDataFilterRequired(event.columns);
    this._agGridService.processOnFilterChanged(event, this.pageContext, this.updatePageContext.bind(this)).then(() => {
      if (updateLiveDataFilter) {
        this.onLiveDataFilterChange(true);
      }
    });
  }

  protected override updatePageContext(updateContextUrl: boolean = true): void {
    this.isFilterActive = Util.checkFilterActive(this.pageContext);
    if (updateContextUrl) {
      this.updateToPageContextUrl();
    }
    this.populateKpiMetrics();
  }

  protected override getSelectionColumnDef(): SelectionColumnDef {
    return this.getSelectColumnDef((params: any) => {
      return {
        showFlag: params.data.invalid == YesNoValue.YES_VALUE,
        flagIcon: "fa-exclamation",
        flagColor: "red",
        toolTip: 'Invalid'
      }
    });
  }

  protected override onViewportChanged(event: ViewportChangedEvent<any>) {
    this.populateKpiMetrics();
  }

  protected override getTableFooterActionButtonPanel() {
    return this.footerActionButtonPanel;
  }

  protected override onFirstDataRendered(event: FirstDataRenderedEvent<any>) {
    super.onFirstDataRendered(event);
    event.api.autoSizeAllColumns();
  }

  private populateKpiMetrics() {
    let filteredRows = this.getRowsAfterFilter();
    let {respondentCompanies, openLinks} = this.calculateKpiMetrics(filteredRows);
    this.kpiSidebar = [
      {
        title: "Respondent Companies",
        value: respondentCompanies
      },
      {
        title: "Open Links",
        value: openLinks
      }
    ];
  }

  private calculateKpiMetrics(rows: OpenLinkInvitee[]) {
    let respondentCompanies = new Set(rows.map(element => element.invitationCompanyCode)).size;
    let openLinks = new Set(rows.map(element => element.openLinkCode)).size;
    return {respondentCompanies, openLinks};
  }
}
